import { Menu, Transition } from '@headlessui/react'
import { Bars3BottomLeftIcon, CheckIcon, DevicePhoneMobileIcon, QrCodeIcon } from '@heroicons/react/24/outline'
import { Dispatch, Fragment, SetStateAction, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  __PARTIAL__APP_SEARCH_USER,
  __PARTIAL__APP_SLOTCHECKIN,
  APP_CATERING_DISTRIBUTION,
  APP_CHECKIN,
  APP_SEARCH_USER,
  APP_SLOTCHECKIN,
  APP_SWITCH_SEASON
} from '../_constants/permissions'
import { EditUser } from '../_features/EditUser'
import { Searchbar } from '../_features/Searchbar'
import { ScheduleEmailChangedUsersDialog } from '../_features/ScheduleEmailChangedUsersDialog'
import { SwitchSeasonDialog } from '../_features/SwitchSeasonDialog'
import { SwitchUserDialog } from '../_features/SwitchUserDialog'
import { useMyself } from '../_features/UserContext'
import { FESTIVAL_SELECTION_PATH } from '../_lib/festival'
import { cacheDatabaseName } from '../_lib/graphql/exchanges/cacheExchange'
import { CateringIcon } from '../_ui/icons/cateringIcon'

type PropTypes = {
  setMobileMenuOpen: Dispatch<SetStateAction<boolean>>;
}

export const Header = ({setMobileMenuOpen}: PropTypes) => {
  const [editUserOpen, setEditUserOpen] = useState(false)
  const [switchUserOpen, setSwitchUserOpen] = useState(false)
  const { id, firstName, surname, profileImageUrl, logout, hasHelperSchedulePermissions } = useMyself() || {};
  const { canAccess } = useMyself() || {}

  const [switchSeasonDialogOpen, setSwitchSeasonDialogOpen] = useState(false)

  const initials = useMemo(() => {
    return `${firstName?.slice(0, 1) || ''}${surname?.slice(0, 1) || ''}`
  }, [firstName, surname])

  return (
    <header className="w-full">
      <div className="relative z-10 flex h-16 shrink-0 border-b border-gray-200 bg-white shadow-sm">
        <button
          type="button"
          className="border-r border-gray-200 px-4 text-gray-500 focus:outline-none focus:ring-0 md:hidden"
          onClick={() => setMobileMenuOpen(true)}
        >
          <span className="sr-only">Open sidebar</span>
          <Bars3BottomLeftIcon className="h-6 w-6" aria-hidden="true" />
        </button>
        <div className="flex flex-1 justify-between px-4 sm:px-6">
          <div className="flex flex-1">
            {canAccess && canAccess([APP_SEARCH_USER, __PARTIAL__APP_SEARCH_USER]) && <Searchbar />}
          </div>

          <div className="ml-2 flex items-center space-x-4 sm:ml-6 sm:space-x-6">
            {((canAccess && canAccess(APP_CATERING_DISTRIBUTION)) || hasHelperSchedulePermissions) && (
              <ScheduleEmailChangedUsersDialog />
            )}
            {((canAccess && canAccess(APP_CATERING_DISTRIBUTION)) || hasHelperSchedulePermissions) && (
              <div className={'text-gray-700'}>
                <Link to={'/catering'} className={'flex items-center text-gray-700 text-sm gap-1'}>
                  <CateringIcon classNames={'w-5 h-5'} />
                  Essen
                </Link>
              </div>
            )}
            {/* Checkin */}
            {canAccess && canAccess(APP_CHECKIN) && !canAccess([APP_SLOTCHECKIN, __PARTIAL__APP_SLOTCHECKIN]) && (
              <Link to={'/checkin'} className={'flex items-center text-gray-700 text-sm gap-1'}>
                <QrCodeIcon className={'w-5 h-5'} />
                Check-In
              </Link>
            )}
            {canAccess && canAccess([APP_SLOTCHECKIN, __PARTIAL__APP_SLOTCHECKIN]) && !canAccess(APP_CHECKIN) && (
              <Link to={'/schichten'} className={'flex items-center text-gray-700 text-sm gap-1'}>
                <QrCodeIcon className={'w-5 h-5'} />
                Schicht Check-In
              </Link>
            )}
            {!(canAccess && canAccess(APP_CHECKIN) && canAccess([APP_SLOTCHECKIN, __PARTIAL__APP_SLOTCHECKIN])) && (
              <Link to={'/self-checkin'} className={'flex items-center text-gray-700 text-sm gap-1'}>
                <DevicePhoneMobileIcon className={'w-5 h-5'} />
                Self Check-In
              </Link>
            )}
            {canAccess && canAccess(APP_CHECKIN) && canAccess([APP_SLOTCHECKIN, __PARTIAL__APP_SLOTCHECKIN]) && (
              <Menu as="div" className="relative flex items-center text-left">
                <Menu.Button
                  className="flex items-center text-gray-700 text-sm gap-1"
                  onClick={event => event.stopPropagation()}
                >
                  <div className={'flex items-center'}>
                    <QrCodeIcon className={'w-5 h-5'} />
                    Check-In
                  </div>
                </Menu.Button>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute top-0 right-0 z-10 mt-2 w-[170px] p-2 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
                    <Menu.Item>
                      <Link to={'/self-checkin'} className={'flex items-center text-gray-700 text-sm gap-1 px-2 py-2'}>
                        <DevicePhoneMobileIcon className={'w-5 h-5'} />
                        Self Check-In
                      </Link>
                    </Menu.Item>
                    <Menu.Item>
                      <Link to={'/checkin'} className={'flex items-center text-gray-700 text-sm gap-1 px-2 py-2'}>
                        <QrCodeIcon className={'w-5 h-5'} />
                        Festival Check-In
                      </Link>
                    </Menu.Item>

                    <Menu.Item>
                      <Link to={'/schichten'} className={'flex items-center text-gray-700 text-sm gap-1 px-2 py-2'}>
                        <CheckIcon className={'w-5 h-5'} />
                        Schicht Check-In
                      </Link>
                    </Menu.Item>
                  </Menu.Items>
                </Transition>
              </Menu>
            )}
            {/* Profile dropdown */}
            <Menu as="div" className="relative shrink-0">
              <div>
                <Menu.Button className="flex rounded-full bg-primary-100 text-sm focus:outline-none">
                  {!profileImageUrl ? (
                    <>
                      <span className="sr-only">Open user menu</span>
                        <span
                          className="flex h-8 w-8 rounded-full text-white"
                        >
                        <span className={'w-6 h-6 m-auto leading-relaxed'}>
                          {initials}
                        </span>
                      </span>
                    </>
                  ) : (
                    <img
                      className="h-8 w-8 rounded-full"
                      src={profileImageUrl}
                      alt=""
                    />
                  )}
                </Menu.Button>
              </div>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none">
                  <Menu.Item>
                    <button
                      onClick={() => setEditUserOpen(true)}
                      className={'w-full text-left flex items-center px-4 py-2 text-sm'}
                    >
                      Mein Profil
                    </button>
                  </Menu.Item>

                  {canAccess && canAccess('ADMIN') && (
                    <Menu.Item>
                      <button
                        onClick={() => setSwitchUserOpen(true)}
                        className={'w-full text-left flex items-center px-4 py-2 text-sm'}
                      >
                        Benutzer wechseln
                      </button>
                    </Menu.Item>
                  )}

                  <Menu.Item>
                    <Link
                      to={FESTIVAL_SELECTION_PATH}
                      className={'w-full text-left flex items-center px-4 py-2 text-sm'}
                    >
                      Festival wechseln
                    </Link>
                  </Menu.Item>

                  <Menu.Item>
                    <button
                      onClick={() => {
                        indexedDB.deleteDatabase(cacheDatabaseName)
                        window.location.reload()
                      }}
                      className={'w-full text-left flex items-center px-4 py-2 text-sm'}
                    >
                      Seite neu laden
                    </button>
                  </Menu.Item>

                  {/*<WebPushSubscription />*/}

                  {canAccess && canAccess(APP_SWITCH_SEASON) && (
                    <Menu.Item>
                      <button
                        onClick={() => setSwitchSeasonDialogOpen(true)}
                        className={'w-full text-left flex items-center px-4 py-2 text-sm'}
                      >
                        Saison wechseln
                      </button>
                    </Menu.Item>
                  )}

                  <Menu.Item>
                    <button
                      className={'w-full text-left flex items-center px-4 py-2 text-sm'}
                      onClick={logout}
                    >
                      Abmelden
                    </button>
                  </Menu.Item>
                </Menu.Items>
              </Transition>
            </Menu>

            <SwitchSeasonDialog
              open={switchSeasonDialogOpen}
              onClose={() => setSwitchSeasonDialogOpen(false)}
            />

            <SwitchUserDialog
              open={switchUserOpen}
              onClose={() => setSwitchUserOpen(false)}
            />

            <EditUser
              userID={id || ''}
              open={editUserOpen}
              onClose={() => setEditUserOpen(false)}
            />
          </div>
        </div>
      </div>
    </header>
  )
}
